/**
 * HorizontalTimeline - Vanilla ES6 replacement for the jQuery plugin
 * "Horizontal Timeline 2.0.5.3" by yCodeTech/CodyHouse.
 *
 * Only the features actually used by GeneaFan are implemented:
 *   - Date positioning on a horizontal bar (responsive intervals)
 *   - Content switching with CSS animations
 *   - Scroll left/right and prev/next navigation buttons
 *   - Filling line (progress bar up to selected date)
 *   - Touch swipe via TouchHandler
 *   - Keyboard navigation (arrow keys, Home/End)
 *   - Responsive breakpoint detection (CSS ::before content)
 *   - Button state management (inactive at boundaries)
 */

import TouchHandler from '../../libs/touch-handler.js';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function deepMerge(target, ...sources) {
    for (const source of sources) {
        for (const key of Object.keys(source)) {
            if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
                target[key] = deepMerge(target[key] || {}, source[key]);
            } else {
                target[key] = source[key];
            }
        }
    }
    return target;
}

function debounce(fn, wait) {
    let timeout;
    return function (...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn.apply(this, args), wait);
    };
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

const DEFAULTS = {
    dateIntervals: {
        desktop: 200,
        tablet: 150,
        mobile: 120,
    },
    dateDisplay: 'year', // 'year' | 'date' | 'monthYear' | 'dayMonth'
    useTouchSwipe: true,
    useNavBtns: true,
    useScrollBtns: true,
    useKeyboardKeys: true,
    iconSvg: {
        scrollLeft:  '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16"><path fill-rule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0"/></svg>',
        scrollRight: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16"><path fill-rule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708"/></svg>',
        prev:        '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16"><path fill-rule="evenodd" d="M1 8a7 7 0 1 0 14 0A7 7 0 0 0 1 8m15 0A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-4.5-.5a.5.5 0 0 1 0 1H5.707l2.147 2.146a.5.5 0 0 1-.708.708l-3-3a.5.5 0 0 1 0-.708l3-3a.5.5 0 1 1 .708.708L5.707 7.5z"/></svg>',
        next:        '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16"><path fill-rule="evenodd" d="M1 8a7 7 0 1 0 14 0A7 7 0 0 0 1 8m15 0A8 8 0 1 1 0 8a8 8 0 0 1 16 0M4.5 7.5a.5.5 0 0 0 0 1h5.793l-2.147 2.146a.5.5 0 0 0 .708.708l3-3a.5.5 0 0 0 0-.708l-3-3a.5.5 0 1 0-.708.708L10.293 7.5z"/></svg>',
    },
    animationClass: {
        base: 'animationSpeed',
        enter: { left: 'enter-left', right: 'enter-right' },
        exit: { left: 'exit-left', right: 'exit-right' },
    },
};

// ---------------------------------------------------------------------------
// HorizontalTimeline
// ---------------------------------------------------------------------------

export default class HorizontalTimeline {

    constructor(element, options = {}) {
        this.el = element;
        this.settings = deepMerge({}, DEFAULTS, options);
        this._abortController = new AbortController();
        this._touchHandlers = [];
        this._totalWidth = 0;
        this._components = {};

        // 1. Parse existing content items
        this._parseContentItems();

        // 2. Build timeline DOM (nav buttons, events bar, filling line)
        this._createTimelineDOM();

        // 3. Create date links on the events bar
        this._createDateLinks();

        // 4. Cache DOM references
        this._cacheComponents();

        // 5. Mark first / last
        this._addIdsAndClasses();

        // 6. Select initial event
        this._selectInitialEvent();

        // 6b. Create leaders band (if data provided)
        if (this.settings.leaders && this.settings.leaders.length > 0) {
            this._createLeadersBand();
        }

        // 6c. Create wars band (if data provided)
        if (this.settings.wars && this.settings.wars.length > 0) {
            this._createWarsBand();
        }

        // 6d. Conditional height class for context bands (on root element for CSS cascading)
        if ((this.settings.leaders?.length > 0) || (this.settings.wars?.length > 0)) {
            this.el.classList.add('has-context-bands');
        }

        // 6e. Setup century bar clicks
        this._setupCenturyBar();

        // 7. Position dates and set widths
        this._setDatePositions();
        this._totalWidth = this._setTimelineWidth();
        this._updateFilling();
        this._updatePeriodHighlight();
        this._positionLeaderSegments();
        this._updateButtonStates(0, this._components.timelineWrapper.offsetWidth - this._totalWidth);

        // 8. Bind event handlers
        this._setupEventHandlers();

        // 9. Show (CSS transition opacity 0 → 1)
        this.el.classList.add('loaded');

        // 10. Lifecycle event
        this.el.dispatchEvent(new CustomEvent('timeline:initialised', {
            bubbles: true,
            detail: { instance: this },
        }));
    }

    // -----------------------------------------------------------------------
    // Public API
    // -----------------------------------------------------------------------

    /** Recalculate positions and widths (e.g. after external DOM changes). */
    refresh() {
        this._cacheComponents();

        // Reset first/last markers
        const links = this._dateLinksArray();
        links.forEach(a => a.classList.remove('first', 'last'));
        if (links.length > 0) {
            links[0].classList.add('first');
            links[links.length - 1].classList.add('last');
        }

        this._setDatePositions();
        this._totalWidth = this._setTimelineWidth();
        this._updateFilling();
        this._updatePeriodHighlight();
        this._positionLeaderSegments();
    }

    /** Tear down: remove all listeners, generated DOM, and state classes. */
    destroy() {
        this._abortController.abort();
        this._touchHandlers.forEach(th => th.destroy());
        this._touchHandlers = [];

        const centuryBar = this.el.querySelector('.century-bar');
        if (centuryBar) centuryBar.remove();

        const timeline = this.el.querySelector('.timeline');
        if (timeline) timeline.remove();

        const leadersBand = this.el.querySelector('.leaders-band');
        if (leadersBand) leadersBand.remove();

        const warsBand = this.el.querySelector('.wars-band');
        if (warsBand) warsBand.remove();

        this.el.classList.remove('loaded');
        this._components = {};
    }

    // -----------------------------------------------------------------------
    // Initialisation helpers
    // -----------------------------------------------------------------------

    _parseContentItems() {
        const items = this.el.querySelectorAll('.events-content li[data-horizontal-timeline]');
        if (items.length === 0) {
            throw new Error('HorizontalTimeline: no li[data-horizontal-timeline] items found');
        }
        this._contentItems = Array.from(items);
        this._dates = this._contentItems.map(li => {
            const data = JSON.parse(li.getAttribute('data-horizontal-timeline'));
            return data.date;
        });
    }

    _createTimelineDOM() {
        const { iconSvg, useNavBtns, useScrollBtns } = this.settings;

        // Left navigation
        let leftNav = '<div class="timeline-navigation" id="leftNav">';
        if (useScrollBtns) {
            leftNav += `<a href="#" class="scroll-left inactive" aria-label="Scroll left">${iconSvg.scrollLeft}</a>`;
        }
        if (useNavBtns) {
            leftNav += `<a href="#" class="prev inactive" aria-label="Previous">${iconSvg.prev}</a>`;
        }
        leftNav += '</div>';

        // Right navigation
        let rightNav = '<div class="timeline-navigation" id="rightNav">';
        if (useNavBtns) {
            rightNav += `<a href="#" class="next inactive" aria-label="Next">${iconSvg.next}</a>`;
        }
        if (useScrollBtns) {
            rightNav += `<a href="#" class="scroll-right inactive" aria-label="Scroll right">${iconSvg.scrollRight}</a>`;
        }
        rightNav += '</div>';

        // Events wrapper
        const eventsWrapper = `
            <div class="events-wrapper" id="timeline-events-wrapper">
                <div class="events" id="timeline-events">
                    <span class="filling-line" id="timeline-filling-line" aria-hidden="true"></span>
                    <span class="period-highlight" id="timeline-period-highlight" aria-hidden="true"></span>
                </div>
            </div>`;

        // Century bar
        const centuryBar = this._buildCenturyBar();

        const html = `${centuryBar}<div class="timeline" id="timeline-container">${leftNav}${eventsWrapper}${rightNav}</div>`;

        // Insert before .events-content (same position as original plugin)
        this.el.insertAdjacentHTML('afterbegin', html);
    }

    _buildCenturyBar() {
        const C_LABELS = ['','Ier','IIe','IIIe','IVe','Ve','VIe','VIIe','VIIIe','IXe','Xe',
            'XIe','XIIe','XIIIe','XIVe','XVe','XVIe','XVIIe','XVIIIe','XIXe','XXe','XXIe'];

        // Collect centuries from timeline dates
        const centuries = new Map(); // century → count of periods
        for (const dateStr of this._dates) {
            const year = parseInt(dateStr.split('/')[2], 10);
            if (isNaN(year)) continue;
            const c = Math.floor((year - 1) / 100) + 1;
            centuries.set(c, (centuries.get(c) || 0) + 1);
        }

        if (centuries.size <= 1) return ''; // No bar needed for single century

        const sorted = [...centuries.keys()].sort((a, b) => a - b);
        const pills = sorted.map(c => {
            const label = (C_LABELS[c] || c + 'e') + ' s.';
            return `<button class="century-pill" data-century="${c}">${label}</button>`;
        }).join('');

        return `<nav class="century-bar" aria-label="Navigation par siècle">${pills}</nav>`;
    }

    _setupCenturyBar() {
        const bar = this.el.querySelector('.century-bar');
        if (!bar) return;

        bar.addEventListener('click', (e) => {
            const btn = e.target.closest('.century-pill');
            if (!btn) return;

            const century = parseInt(btn.dataset.century, 10);
            const targetYear = (century - 1) * 100 + 1;

            // Find the first date link whose year is in this century
            this._cacheComponents();
            const links = this._dateLinksArray();
            const target = links.find(a => {
                const year = parseInt(a.textContent, 10);
                return year >= targetYear && year < targetYear + 100;
            });

            if (target) {
                links.forEach(a => { a.classList.remove('selected'); a.removeAttribute('id'); });
                target.classList.add('selected');
                target.id = 'timeline-selected-date';

                this._totalWidth = this._setTimelineWidth();
                this._updateOlderEvents(target);
                this._updateFilling(target);
                this._updatePeriodHighlight(target);
                this._updateVisibleContent(target);
                this._updateTimelinePosition(target);
            }

            // Update active pill
            bar.querySelectorAll('.century-pill').forEach(p => p.classList.toggle('active', p === btn));
        });

        // Auto-highlight the century of the currently selected period
        this._updateCenturyHighlight();
    }

    _updateCenturyHighlight() {
        const bar = this.el.querySelector('.century-bar');
        if (!bar) return;

        const selected = this.el.querySelector('.events a.selected[data-horizontal-timeline]');
        if (!selected) return;

        const year = parseInt(selected.textContent, 10);
        if (isNaN(year)) return;
        const century = Math.floor((year - 1) / 100) + 1;

        bar.querySelectorAll('.century-pill').forEach(p =>
            p.classList.toggle('active', parseInt(p.dataset.century, 10) === century)
        );
    }

    _createDateLinks() {
        const eventsBar = this.el.querySelector('.events');
        this._contentItems.forEach((_li, index) => {
            const dateStr = this._dates[index];
            const a = document.createElement('a');
            a.href = '#';
            a.setAttribute('data-horizontal-timeline', JSON.stringify({ date: dateStr }));
            a.textContent = this._formatDateDisplay(dateStr);
            eventsBar.appendChild(a);
        });
    }

    _formatDateDisplay(dateStr) {
        // dateStr is "DD/MM/YYYY" format from timelineStore
        const parts = dateStr.split('/');
        if (parts.length !== 3) return dateStr;

        const day = parts[0], month = parts[1], year = parts[2];

        switch (this.settings.dateDisplay) {
            case 'year':      return year;
            case 'monthYear': return `${month}/${year}`;
            case 'dayMonth':  return `${day}/${month}`;
            case 'date':      return dateStr;
            default:          return year;
        }
    }

    _cacheComponents() {
        const c = this._components;
        c.eventsContent = this.el.querySelector('.events-content');
        c.contentItems = this.el.querySelectorAll('.events-content li[data-horizontal-timeline]');
        c.timelineWrapper = this.el.querySelector('.events-wrapper');
        c.eventsBar = this.el.querySelector('.events');
        c.fillingLine = this.el.querySelector('.filling-line');
        c.periodHighlight = this.el.querySelector('.period-highlight');
        c.dateLinks = this.el.querySelectorAll('.events a[data-horizontal-timeline]');
        c.leadersBand = this.el.querySelector('.leaders-band');
        c.warsBand = this.el.querySelector('.wars-band');
    }

    _addIdsAndClasses() {
        const links = this._dateLinksArray();
        if (links.length === 0) return;
        links[0].classList.add('first');
        links[links.length - 1].classList.add('last');

        // Also mark first/last content items
        const items = Array.from(this._components.contentItems);
        if (items.length > 0) {
            items[0].id = 'first';
            items[items.length - 1].id = 'last';
        }
    }

    _selectInitialEvent() {
        const links = this._dateLinksArray();
        if (links.length === 0) return;

        // Select first date link
        links[0].classList.add('selected');
        links[0].id = 'timeline-selected-date';

        // Select matching content item
        const firstDate = this._dates[0];
        this._contentItems.forEach(li => {
            const d = JSON.parse(li.getAttribute('data-horizontal-timeline'));
            if (d.date === firstDate) {
                li.classList.add('selected');
                li.id = 'timeline-selected-content';
            }
        });

        // Set initial content height to match selected item
        const selected = this._components.eventsContent.querySelector('li.selected');
        if (selected) {
            this._components.eventsContent.style.height = selected.offsetHeight + 'px';
        }
    }

    // -----------------------------------------------------------------------
    // Layout
    // -----------------------------------------------------------------------

    _setDatePositions() {
        const interval = this._getResponsiveInterval();
        const links = this._dateLinksArray();
        if (links.length === 0) return;

        const mq = this._checkMQ();

        links.forEach((a, i) => {
            if (i === 0 && mq === 'mobile') {
                a.style.left = '0px';
                a.style.paddingLeft = '10px';
            } else {
                a.style.left = (i * interval) + 'px';
            }
        });
    }

    _setTimelineWidth() {
        const links = this._dateLinksArray();
        const wrapper = this._components.timelineWrapper;
        const eventsBar = this._components.eventsBar;
        if (!wrapper || !eventsBar) return 0;

        const wrapperWidth = wrapper.offsetWidth;

        let lastPosition = 0;
        if (links.length > 0) {
            const last = links[links.length - 1];
            lastPosition = parseFloat(last.style.left || '0') + 100;
        }

        const totalWidth = Math.max(lastPosition, wrapperWidth);
        eventsBar.style.width = totalWidth + 'px';

        if (this._components.leadersBand) {
            this._components.leadersBand.style.width = totalWidth + 'px';
        }
        if (this._components.warsBand) {
            this._components.warsBand.style.width = totalWidth + 'px';
        }

        return totalWidth;
    }

    _updateFilling(selectedLink) {
        const selected = selectedLink || this._components.eventsBar?.querySelector('a.selected');
        if (!selected || !this._totalWidth) return;

        const eventLeft = parseFloat(selected.style.left || '0');
        const eventWidth = selected.offsetWidth;
        const center = eventLeft + eventWidth / 2;
        const scaleValue = center / this._totalWidth;

        this._components.fillingLine.style.transform = `scaleX(${scaleValue})`;
    }

    _updatePeriodHighlight(selectedLink) {
        const selected = selectedLink || this._components.eventsBar?.querySelector('a.selected');
        const highlight = this._components.periodHighlight;
        if (!selected || !highlight) return;

        const links = this._dateLinksArray();
        const idx = links.indexOf(selected);
        if (idx === -1) return;

        // Center on the dot (::after is left:40%, width:12px → center = left + 40%*width + 6)
        const dotCenter = (a) => {
            const l = parseFloat(a.style.left || '0');
            return l + 0.4 * a.offsetWidth + 6;
        };
        const startX = dotCenter(selected);

        let endX;
        if (idx < links.length - 1) {
            endX = dotCenter(links[idx + 1]);
        } else {
            endX = startX + this._getResponsiveInterval();
        }

        highlight.style.left = startX + 'px';
        highlight.style.width = (endX - startX) + 'px';
    }

    // -----------------------------------------------------------------------
    // Context bands (leaders + wars) — shared positioning engine
    // -----------------------------------------------------------------------

    /**
     * Build year → pixel anchor points from actual date link positions.
     * The timeline is ordinal (equal spacing per period), so we must
     * interpolate between anchors rather than use a uniform px-per-year ratio.
     */
    _buildAnchors() {
        const links = this._dateLinksArray();
        if (links.length === 0) return [];

        return links.map(a => {
            const date = JSON.parse(a.getAttribute('data-horizontal-timeline')).date;
            const linkLeft = parseFloat(a.style.left || '0');
            // Align with the dot (::after at left:40%, width:12px → center)
            const dotX = linkLeft + 0.4 * a.offsetWidth + 6;
            return {
                year: parseInt(date.split('/')[2], 10),
                px:   dotX,
            };
        });
    }

    /**
     * Interpolate: fractional year → pixel position.
     * Handles 3 cases: before first anchor, between anchors, after last.
     */
    _yearToPixel(y) {
        const anchors = this._anchors;
        if (!anchors || anchors.length === 0) return 0;
        if (anchors.length < 2) return anchors[0].px;

        // Before first anchor — extrapolate with first segment's rate
        if (y <= anchors[0].year) {
            const rate = (anchors[1].px - anchors[0].px) / (anchors[1].year - anchors[0].year);
            return anchors[0].px + (y - anchors[0].year) * rate;
        }
        // After last anchor — extrapolate with last segment's rate
        const n = anchors.length;
        if (y >= anchors[n - 1].year) {
            const rate = (anchors[n - 1].px - anchors[n - 2].px) / (anchors[n - 1].year - anchors[n - 2].year);
            return anchors[n - 1].px + (y - anchors[n - 1].year) * rate;
        }
        // Between two anchors — linear interpolation
        for (let i = 0; i < n - 1; i++) {
            if (y >= anchors[i].year && y < anchors[i + 1].year) {
                const ratio = (y - anchors[i].year) / (anchors[i + 1].year - anchors[i].year);
                return anchors[i].px + ratio * (anchors[i + 1].px - anchors[i].px);
            }
        }
        return anchors[n - 1].px;
    }

    // -----------------------------------------------------------------------
    // Leaders band
    // -----------------------------------------------------------------------

    _createLeadersBand() {
        const wrapper = this._components.timelineWrapper;
        if (!wrapper) return;

        const band = document.createElement('div');
        band.className = 'leaders-band';
        band.id = 'timeline-leaders-band';
        band.setAttribute('aria-hidden', 'true');
        wrapper.appendChild(band);
        this._components.leadersBand = band;

        for (const leader of this.settings.leaders) {
            const seg = document.createElement('div');
            seg.className = `leader-segment ${leader.regimeClass || ''}`;
            const fmtDate = (iso) => { const [y, m, d] = iso.split('-'); return `${d}/${m}/${y}`; };
            seg.title = `${leader.title}\u00a0: ${leader.name} (${fmtDate(leader.start)}\u2013${fmtDate(leader.end)})`;

            const nameSpan = document.createElement('span');
            nameSpan.className = 'leader-name';
            nameSpan.textContent = leader.name;
            seg.appendChild(nameSpan);

            band.appendChild(seg);
        }
    }

    _positionLeaderSegments() {
        const band = this._components.leadersBand;
        if (!band) return;

        const links = this._dateLinksArray();
        if (links.length === 0) return;

        const yearsGroup = this.settings.yearsGroup || 5;

        // Build shared anchors (used by leaders, wars, and year ticks)
        this._anchors = this._buildAnchors();
        const firstYear = this._anchors[0].year;
        const lastYear  = this._anchors[this._anchors.length - 1].year + yearsGroup;

        band.style.width = this._totalWidth + 'px';

        const segments = band.querySelectorAll('.leader-segment');
        const leaders = this.settings.leaders;

        segments.forEach((seg, i) => {
            const leader = leaders[i];
            if (!leader) { seg.style.display = 'none'; return; }

            const clampedStart = Math.max(leader.startFrac, firstYear);
            const clampedEnd   = Math.min(leader.endFrac, lastYear);

            if (clampedStart >= clampedEnd) {
                seg.style.display = 'none';
                return;
            }

            const leftPx  = this._yearToPixel(clampedStart);
            const rightPx = this._yearToPixel(clampedEnd);

            seg.style.display = '';
            seg.style.left  = leftPx + 'px';
            seg.style.width = (rightPx - leftPx) + 'px';
        });

        // Year tick marks on the events bar between 5-year markers
        this._updateYearTicks(firstYear, lastYear, yearsGroup);

        // Position wars band using the same anchors
        this._positionWarSegments(firstYear, lastYear);
    }

    // -----------------------------------------------------------------------
    // Wars band
    // -----------------------------------------------------------------------

    _createWarsBand() {
        const wrapper = this._components.timelineWrapper;
        if (!wrapper) return;

        const band = document.createElement('div');
        band.className = 'wars-band';
        band.id = 'timeline-wars-band';
        band.setAttribute('aria-hidden', 'true');
        wrapper.appendChild(band);
        this._components.warsBand = band;

        for (const war of this.settings.wars) {
            const seg = document.createElement('div');
            seg.className = 'war-segment';
            seg.title = `${war.name} (${war.start}\u2013${war.end})`;

            const nameSpan = document.createElement('span');
            nameSpan.className = 'war-name';
            nameSpan.textContent = war.name;
            seg.appendChild(nameSpan);

            band.appendChild(seg);
        }
    }

    _positionWarSegments(firstYear, lastYear) {
        const band = this._components.warsBand;
        if (!band || !this._anchors || this._anchors.length === 0) return;

        band.style.width = this._totalWidth + 'px';

        const segments = band.querySelectorAll('.war-segment');
        const wars = this.settings.wars;

        segments.forEach((seg, i) => {
            const war = wars[i];
            if (!war) { seg.style.display = 'none'; return; }

            const clampedStart = Math.max(war.startFrac, firstYear);
            const clampedEnd   = Math.min(war.endFrac, lastYear);

            if (clampedStart >= clampedEnd) {
                seg.style.display = 'none';
                return;
            }

            const leftPx  = this._yearToPixel(clampedStart);
            const rightPx = this._yearToPixel(clampedEnd);

            seg.style.display = '';
            seg.style.left  = leftPx + 'px';
            seg.style.width = Math.max(rightPx - leftPx, 3) + 'px'; // min 3px for short wars
        });
    }

    // -----------------------------------------------------------------------
    // Year ticks
    // -----------------------------------------------------------------------

    _updateYearTicks(firstYear, lastYear, yearsGroup) {
        const eventsBar = this._components.eventsBar;
        if (!eventsBar) return;

        // Remove previous ticks
        eventsBar.querySelectorAll('.year-tick').forEach(t => t.remove());

        const frag = document.createDocumentFragment();
        for (let y = firstYear; y <= lastYear; y++) {
            // Skip years that fall on 5-year boundaries (already have dot markers)
            if ((y - firstYear) % yearsGroup === 0) continue;

            const tick = document.createElement('div');
            tick.className = 'year-tick';
            tick.style.left = this._yearToPixel(y) + 'px';
            frag.appendChild(tick);
        }
        eventsBar.appendChild(frag);
    }

    // -----------------------------------------------------------------------
    // Navigation
    // -----------------------------------------------------------------------

    _updateVisibleContent(newDateLink) {
        const dateStr = JSON.parse(newDateLink.getAttribute('data-horizontal-timeline')).date;
        const eventsContent = this._components.eventsContent;
        const current = eventsContent.querySelector('li.selected');
        const items = Array.from(this._components.contentItems);

        const newContent = items.find(li => {
            const d = JSON.parse(li.getAttribute('data-horizontal-timeline'));
            return d.date === dateStr;
        });
        if (!newContent || newContent === current) return;

        const { enter, exit, base } = this.settings.animationClass;
        const allAnimClasses = [exit.right, exit.left, enter.left, enter.right, base];
        const currentIdx = items.indexOf(current);
        const newIdx = items.indexOf(newContent);
        const goingRight = newIdx > currentIdx;

        const classEntering = goingRight ? enter.right : enter.left;
        const classExiting = goingRight ? exit.left : exit.right;

        // Animate new content in
        newContent.classList.add('selected', classEntering, base);
        newContent.id = 'timeline-selected-content';

        // Animate old content out
        if (current) {
            current.classList.add(classExiting, base);
            current.classList.remove('selected');
            current.removeAttribute('id');

            current.addEventListener('animationend', () => {
                items.forEach(li => li.classList.remove(...allAnimClasses));
            }, { once: true });
        }

        // Update content container height to match new content
        eventsContent.style.height = newContent.offsetHeight + 'px';

        // Dispatch event
        this.el.dispatchEvent(new CustomEvent('timeline:eventChanged', {
            bubbles: true,
            detail: { date: dateStr },
        }));
    }

    _showNewContent(direction) {
        const items = Array.from(this._components.contentItems);
        const current = this._components.eventsContent.querySelector('li.selected');
        const idx = items.indexOf(current);
        const newIdx = direction === 'next' ? idx + 1 : idx - 1;
        if (newIdx < 0 || newIdx >= items.length) return;

        const newContent = items[newIdx];
        const dateStr = JSON.parse(newContent.getAttribute('data-horizontal-timeline')).date;

        // Find matching date link
        const links = this._dateLinksArray();
        const newDateLink = links.find(a => {
            const d = JSON.parse(a.getAttribute('data-horizontal-timeline'));
            return d.date === dateStr;
        });
        if (!newDateLink) return;

        // Update date selection
        links.forEach(a => a.classList.remove('selected'));
        newDateLink.classList.add('selected');
        newDateLink.id = 'timeline-selected-date';

        this._updateVisibleContent(newDateLink);
        this._updateFilling(newDateLink);
        this._updatePeriodHighlight(newDateLink);
        this._updateOlderEvents(newDateLink);
        this._updateTimelinePosition(newDateLink);
        this._updateCenturyHighlight();
    }

    _updateSlide(direction) {
        const current = this._getTranslateValue();
        const wrapperWidth = this._components.timelineWrapper.offsetWidth;
        if (direction === 'right') {
            this._translateTimeline(current - wrapperWidth, wrapperWidth - this._totalWidth);
        } else {
            this._translateTimeline(current + wrapperWidth);
        }
    }

    _updateTimelinePosition(dateLink) {
        const eventLeft = parseFloat(dateLink.style.left || '0');
        const wrapperWidth = this._components.timelineWrapper.offsetWidth;
        this._translateTimeline(-eventLeft + wrapperWidth / 2, wrapperWidth - this._totalWidth);
    }

    _translateTimeline(value, minValue) {
        value = Math.min(0, value); // never positive (can't scroll before start)
        if (minValue !== undefined) {
            value = Math.max(minValue, value); // never beyond end
        }
        this._components.eventsBar.style.transform = `translateX(${value}px)`;
        if (this._components.leadersBand) {
            this._components.leadersBand.style.transform = `translateX(${value}px)`;
        }
        if (this._components.warsBand) {
            this._components.warsBand.style.transform = `translateX(${value}px)`;
        }
        this._updateButtonStates(value, minValue);
    }

    _updateOlderEvents(selectedLink) {
        const links = this._dateLinksArray();
        const idx = links.indexOf(selectedLink);
        links.forEach((a, i) => {
            a.classList.toggle('older-event', i < idx);
        });
    }

    _updateButtonStates(translateValue, totalTranslateValue) {
        const links = this._dateLinksArray();
        const selected = this._components.eventsBar.querySelector('a.selected');
        const first = links[0];
        const last = links[links.length - 1];

        const setInactive = (selector, inactive) => {
            this.el.querySelectorAll(`.timeline-navigation ${selector}`).forEach(btn => {
                btn.classList.toggle('inactive', inactive);
            });
        };

        // Prev / Next
        if (links.length <= 1) {
            setInactive('.prev', true);
            setInactive('.next', true);
        } else {
            setInactive('.prev', selected === first);
            setInactive('.next', selected === last);
        }

        // Scroll left / right
        const wrapperWidth = this._components.timelineWrapper.offsetWidth;
        const totalWidth = this._components.eventsBar.offsetWidth;

        if (wrapperWidth >= totalWidth) {
            setInactive('.scroll-left', true);
            setInactive('.scroll-right', true);
        } else {
            setInactive('.scroll-left', translateValue === 0);
            setInactive('.scroll-right', translateValue === totalTranslateValue);
        }
    }

    // -----------------------------------------------------------------------
    // Event handlers
    // -----------------------------------------------------------------------

    _setupEventHandlers() {
        const signal = this._abortController.signal;

        // --- Click on navigation buttons (event delegation) ---
        this.el.addEventListener('click', (e) => {
            const navLink = e.target.closest('.timeline-navigation a');
            if (!navLink) return;
            e.preventDefault();
            if (navLink.classList.contains('inactive')) return;

            this._cacheComponents();
            this._totalWidth = this._setTimelineWidth();

            if (navLink.classList.contains('next'))         this._showNewContent('next');
            else if (navLink.classList.contains('prev'))    this._showNewContent('prev');
            else if (navLink.classList.contains('scroll-right')) this._updateSlide('right');
            else if (navLink.classList.contains('scroll-left'))  this._updateSlide('left');
        }, { signal });

        // --- Click on date links ---
        this.el.addEventListener('click', (e) => {
            const dateLink = e.target.closest('.events a[data-horizontal-timeline]');
            if (!dateLink) return;
            e.preventDefault();

            this._cacheComponents();
            const links = this._dateLinksArray();
            links.forEach(a => { a.classList.remove('selected'); a.removeAttribute('id'); });
            dateLink.classList.add('selected');
            dateLink.id = 'timeline-selected-date';

            this._updateOlderEvents(dateLink);
            this._totalWidth = this._setTimelineWidth();
            this._updateFilling(dateLink);
            this._updatePeriodHighlight(dateLink);
            this._updateVisibleContent(dateLink);
            this._updateTimelinePosition(dateLink);
        }, { signal });

        // --- Touch swipe ---
        if (this.settings.useTouchSwipe) {
            // Swipe on event content → prev/next event
            const contentEl = this._components.eventsContent;
            if (contentEl) {
                const contentTouch = new TouchHandler(contentEl, {
                    threshold: 75,
                    swipeLeft: () => { this._cacheComponents(); this._totalWidth = this._setTimelineWidth(); this._showNewContent('next'); },
                    swipeRight: () => { this._cacheComponents(); this._totalWidth = this._setTimelineWidth(); this._showNewContent('prev'); },
                });
                this._touchHandlers.push(contentTouch);
                contentEl.classList.add('touch-enabled');
            }

            // Swipe on timeline wrapper → scroll timeline
            const wrapperEl = this._components.timelineWrapper;
            if (wrapperEl) {
                const wrapperTouch = new TouchHandler(wrapperEl, {
                    threshold: 30,
                    swipeLeft: () => { this._cacheComponents(); this._totalWidth = this._setTimelineWidth(); this._updateSlide('right'); },
                    swipeRight: () => { this._cacheComponents(); this._totalWidth = this._setTimelineWidth(); this._updateSlide('left'); },
                });
                this._touchHandlers.push(wrapperTouch);
                wrapperEl.classList.add('touch-enabled');
            }
        }

        // --- Keyboard navigation ---
        if (this.settings.useKeyboardKeys) {
            document.addEventListener('keydown', (e) => {
                if (!this._isInViewport()) return;

                // Ignore if user is typing in an input/textarea
                const tag = e.target.tagName;
                if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

                // Shift+Arrow is reserved for year-pill sub-navigation
                if (e.shiftKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) return;

                this._cacheComponents();
                this._totalWidth = this._setTimelineWidth();

                switch (e.key) {
                    case 'ArrowLeft':
                        e.preventDefault();
                        this._showNewContent('prev');
                        break;
                    case 'ArrowRight':
                        e.preventDefault();
                        this._showNewContent('next');
                        break;
                    case 'Home':
                        e.preventDefault();
                        this._goToEdge('first');
                        break;
                    case 'End':
                        e.preventDefault();
                        this._goToEdge('last');
                        break;
                }
            }, { signal });
        }

        // Historical card summary is shown inline on hover (no tooltip needed)

        // --- Window resize (debounced) ---
        const onResize = debounce(() => {
            this._cacheComponents();
            this._setDatePositions();
            this._totalWidth = this._setTimelineWidth();
            this._updateFilling();
            this._updatePeriodHighlight();
            this._positionLeaderSegments();
        }, 250);
        window.addEventListener('resize', onResize, { signal });
    }

    _goToEdge(which) {
        const links = this._dateLinksArray();
        if (links.length === 0) return;
        const target = which === 'first' ? links[0] : links[links.length - 1];
        const selected = this._components.eventsBar.querySelector('a.selected');
        if (target === selected) return;

        links.forEach(a => { a.classList.remove('selected'); a.removeAttribute('id'); });
        target.classList.add('selected');
        target.id = 'timeline-selected-date';

        this._updateOlderEvents(target);
        this._updateFilling(target);
        this._updatePeriodHighlight(target);
        this._updateVisibleContent(target);
        this._updateTimelinePosition(target);
    }

    // -----------------------------------------------------------------------
    // Utilities
    // -----------------------------------------------------------------------

    _dateLinksArray() {
        return Array.from(this._components.dateLinks || []);
    }

    _getTranslateValue() {
        const style = getComputedStyle(this._components.eventsBar);
        const matrix = style.transform;
        if (!matrix || matrix === 'none') return 0;
        const values = matrix.split('(')[1].split(')')[0].split(',');
        return Number(values[4]) || 0;
    }

    _getResponsiveInterval() {
        const mq = this._checkMQ();
        const intervals = this.settings.dateIntervals;
        const val = intervals[mq] || intervals.desktop;
        return Math.max(val, 120);
    }

    _checkMQ() {
        // Reads CSS ::before content to detect breakpoint (desktop/tablet/mobile)
        return getComputedStyle(this.el, '::before').content.replace(/['"]/g, '');
    }

    _isInViewport() {
        const rect = this.el.getBoundingClientRect();
        return (
            rect.top < window.innerHeight &&
            rect.bottom > 0 &&
            rect.left < window.innerWidth &&
            rect.right > 0
        );
    }
}
